<script>
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import TemplateParser from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplateParser.vue';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import inboxesApi from 'dashboard/api/inboxes.js';
import { useAlert } from 'dashboard/composables';
import TemplateCreator from './TemplateCreator.vue';

export default {
  components: {
    SettingsSection,
    TemplateParser,
    Modal,
    NextButton,
    TemplateCreator,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showCreateModal: false,
      showTemplateModal: false,
      selectedTemplate: null,
      isSyncing: false,
    };
  },
  computed: {
    whatsAppTemplates() {
      const templates = this.$store.getters[
        'inboxes/getManageableWhatsAppTemplates'
      ](this.inbox.id);
      return Array.isArray(templates) ? templates : [];
    },
    hasTemplates() {
      return this.whatsAppTemplates.length > 0;
    },
    isWhatsAppChannel() {
      return this.inbox.channel_type === 'Channel::Whatsapp';
    },
  },
  methods: {
    openCreator() {
      this.showCreateModal = true;
    },
    onCloseCreate() {
      this.showCreateModal = false;
    },
    async syncTemplates() {
      if (!this.isWhatsAppChannel) return;

      this.isSyncing = true;
      try {
        const response = await inboxesApi.syncTemplates(this.inbox.id);
        this.$store.dispatch('inboxes/setWhatsAppTemplates', {
          inboxId: this.inbox.id,
          templates: response.data.templates,
        });
        useAlert(this.$t('INBOX_MGMT.TEMPLATES.SYNC_SUCCESS'));
      } catch (error) {
        console.error('Template sync failed:', error);
        const errorMessage =
          error.response?.data?.error ||
          this.$t('INBOX_MGMT.TEMPLATES.SYNC_ERROR');
        useAlert(errorMessage);
      } finally {
        this.isSyncing = false;
      }
    },
    getStatusBadgeClass(status) {
      const statusLower = status?.toLowerCase();
      switch (statusLower) {
        case 'approved':
          return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        case 'pending':
          return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        case 'rejected':
          return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        default:
          return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      }
    },
    getStatusText(status) {
      const statusLower = status?.toLowerCase();
      switch (statusLower) {
        case 'approved':
          return this.$t('INBOX_MGMT.TEMPLATES.STATUS.APPROVED');
        case 'pending':
          return this.$t('INBOX_MGMT.TEMPLATES.STATUS.PENDING');
        case 'rejected':
          return this.$t('INBOX_MGMT.TEMPLATES.STATUS.REJECTED');
        default:
          return this.$t('INBOX_MGMT.TEMPLATES.STATUS.UNKNOWN');
      }
    },
    onTemplateSelect(template) {
      this.selectedTemplate = template;
      this.showTemplateModal = true;
    },
    getTemplateComponents(template) {
      return template.components || [];
    },
    getTemplateBody(template) {
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      return bodyComponent?.text || 'No body content';
    },
    getTemplateHeader(template) {
      const headerComponent = template.components?.find(
        c => c.type === 'HEADER'
      );
      return headerComponent?.text || null;
    },
    getTemplateFooter(template) {
      const footerComponent = template.components?.find(
        c => c.type === 'FOOTER'
      );
      return footerComponent?.text || null;
    },
    onCloseModal() {
      this.showTemplateModal = false;
      this.selectedTemplate = null;
    },
    onSendMessage() {
      // Handle sending template message
      // This would typically dispatch an action to send the message
      this.onCloseModal();
    },
    onResetTemplate() {
      this.selectedTemplate = null;
    },
    onTemplatesSynced(templates) {
      this.$store.dispatch('inboxes/setWhatsAppTemplates', {
        inboxId: this.inbox.id,
        templates,
      });
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.TEMPLATES.TITLE')"
      :sub-title="$t('INBOX_MGMT.TEMPLATES.SUBTITLE')"
    >
      <!-- Top toolbar: New Template toggle button -->
      <div class="mb-4 flex items-center justify-end">
        <NextButton
          size="sm"
          variant="primary"
          :label="$t('INBOX_MGMT.TEMPLATES.NEW_TEMPLATE_BUTTON')"
          @click="openCreator"
        />
      </div>
      <!-- Sync Section for WhatsApp Channels -->
      <div v-if="isWhatsAppChannel" class="mb-6">
        <div
          class="bg-n-background outline-n-container outline outline-1 rounded-lg p-4"
        >
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('INBOX_MGMT.TEMPLATES.SYNC_BUTTON') }}
              </h3>
              <p class="text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.TEMPLATES.SYNC_DESCRIPTION') }}
              </p>
            </div>
            <NextButton
              :loading="isSyncing"
              :label="
                isSyncing
                  ? $t('INBOX_MGMT.TEMPLATES.SYNCING')
                  : $t('INBOX_MGMT.TEMPLATES.SYNC_BUTTON')
              "
              variant="primary"
              size="sm"
              @click="syncTemplates"
            />
          </div>
          <p class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.TEMPLATES.LAST_SYNC_INFO') }}
          </p>
        </div>
      </div>

      <div v-if="hasTemplates" class="mb-6">
        <div class="mb-4">
          <p class="text-sm text-n-slate-11 mb-4">
            {{ $t('INBOX_MGMT.TEMPLATES.DESCRIPTION') }}
          </p>
        </div>

        <div
          class="bg-n-background outline-n-container outline outline-1 rounded-lg p-4"
        >
          <div class="grid gap-4">
            <div
              v-for="template in whatsAppTemplates"
              :key="template.id"
              class="border border-n-weak rounded-lg p-4 hover:bg-n-alpha-2 cursor-pointer"
              @click="onTemplateSelect(template)"
            >
              <div class="flex items-center justify-between mb-2">
                <h3 class="font-medium text-n-slate-12">{{ template.name }}</h3>
                <div class="flex items-center gap-2">
                  <span
                    class="inline-block px-2 py-1 text-xs bg-n-slate-3 rounded-lg text-n-slate-12"
                  >
                    {{ template.language }}
                  </span>
                  <span
                    class="inline-block px-2 py-1 text-xs rounded-lg font-medium"
                    :class="getStatusBadgeClass(template.status)"
                  >
                    {{ getStatusText(template.status) }}
                  </span>
                </div>
              </div>

              <div class="mb-3">
                <p class="text-sm font-medium text-n-slate-11 mb-1">
                  {{ $t('INBOX_MGMT.TEMPLATES.TEMPLATE_BODY') }}
                </p>
                <p class="text-sm text-n-slate-12 line-clamp-2">
                  {{ getTemplateBody(template) }}
                </p>
              </div>

              <div class="flex items-center justify-between">
                <span class="text-xs text-n-slate-11">
                  {{ $t('INBOX_MGMT.TEMPLATES.CATEGORY') }}:
                  {{ template.category }}
                </span>
                <NextButton
                  size="sm"
                  :label="$t('INBOX_MGMT.TEMPLATES.VIEW_DETAILS')"
                  variant="secondary"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-8">
        <div class="mb-4">
          <fluent-icon
            icon="document-outline"
            class="text-n-slate-8 mx-auto"
            size="48"
          />
        </div>
        <h3 class="text-lg font-medium text-n-slate-12 mb-2">
          {{ $t('INBOX_MGMT.TEMPLATES.NO_TEMPLATES_TITLE') }}
        </h3>
        <p class="text-n-slate-11">
          {{ $t('INBOX_MGMT.TEMPLATES.NO_TEMPLATES_DESCRIPTION') }}
        </p>
      </div>
    </SettingsSection>

    <!-- Template Modal -->
    <Modal
      :show="showTemplateModal"
      :on-close="onCloseModal"
      size="modal-big"
      @update:show="showTemplateModal = $event"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="text-lg font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.TEMPLATES.MODAL_TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ selectedTemplate?.name }}
          </p>
        </div>

        <div class="modal-body">
          <div v-if="selectedTemplate" class="space-y-6">
            <!-- Template Header -->
            <div
              v-if="getTemplateHeader(selectedTemplate)"
              class="border-b border-n-weak pb-4"
            >
              <h3 class="text-sm font-medium text-n-slate-11 mb-2">Header</h3>
              <p class="text-sm text-n-slate-12 bg-n-slate-3 p-3 rounded-lg">
                {{ getTemplateHeader(selectedTemplate) }}
              </p>
            </div>

            <!-- Template Body -->
            <div class="border-b border-n-weak pb-4">
              <h3 class="text-sm font-medium text-n-slate-11 mb-2">Body</h3>
              <p class="text-sm text-n-slate-12 bg-n-slate-3 p-3 rounded-lg">
                {{ getTemplateBody(selectedTemplate) }}
              </p>
            </div>

            <!-- Template Footer -->
            <div
              v-if="getTemplateFooter(selectedTemplate)"
              class="border-b border-n-weak pb-4"
            >
              <h3 class="text-sm font-medium text-n-slate-11 mb-2">Footer</h3>
              <p class="text-sm text-n-slate-12 bg-n-slate-3 p-3 rounded-lg">
                {{ getTemplateFooter(selectedTemplate) }}
              </p>
            </div>

            <!-- Template Details -->
            <div class="space-y-3">
              <div class="flex justify-between">
                <span class="text-sm font-medium text-n-slate-11">Language:</span>
                <span class="text-sm text-n-slate-12">{{
                  selectedTemplate.language
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm font-medium text-n-slate-11">Category:</span>
                <span class="text-sm text-n-slate-12">{{
                  selectedTemplate.category
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm font-medium text-n-slate-11">Status:</span>
                <span
                  class="text-sm px-2 py-1 rounded-lg font-medium"
                  :class="getStatusBadgeClass(selectedTemplate.status)"
                >
                  {{ getStatusText(selectedTemplate.status) }}
                </span>
              </div>
              <div
                v-if="selectedTemplate.namespace"
                class="flex justify-between"
              >
                <span class="text-sm font-medium text-n-slate-11">Namespace:</span>
                <span class="text-sm text-n-slate-12">{{
                  selectedTemplate.namespace
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Full-width Creator Modal -->
    <Modal
      :show="showCreateModal"
      :on-close="onCloseCreate"
      :full-width="true"
      @update:show="showCreateModal = $event"
    >
      <div class="modal-content w-full">
        <div class="mb-6 pb-4 border-b border-n-weak">
          <h2 class="text-lg font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.TEMPLATES.CREATE.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.TEMPLATES.CREATE.SUBTITLE') }}
          </p>
        </div>
        <TemplateCreator @synced="onTemplatesSynced" />
      </div>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.modal-content {
  @apply p-6;
}

.modal-header {
  @apply mb-6 pb-4 border-b border-n-weak;
}

.modal-body {
  @apply min-h-[400px];
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
